import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Checkbox,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import MessageIcon from "@mui/icons-material/Message";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CategoryIcon from "@mui/icons-material/Category";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useDispatch, useSelector } from "react-redux";
import { getAuthToken } from "../../utils/getAuthToken";
import {
  addNodeToBranch,
  updateNodeInBranch,
  setNodeNextBranches,
  addBranch,
  removeBranch,
} from "../../redux/Slice/Workflow/workflowSlice";
import { fetchTeams, fetchTeamMembers } from "../../redux/Slice/Settings/settingsSlice";
import { fetchCatalogsFromDb } from "../../redux/Slice/Catalog/catalogSlice";
import CatalogSend from "../Inbox/Features/TemplateSendFeatures/CatalogSend";
import BusinessHoursEditor, { DEFAULT_BUSINESS_HOURS_VALUE, isBusinessHoursValueValid } from "./BusinessHoursEditor";
import {
  BUSINESS_HOURS_BRANCH_ACTION,
  isBusinessHoursBranchAction,
  buildBusinessHoursConfig,
  applyBusinessHoursBranches,
} from "./businessHoursBranch";
import ContactFieldsEditor from "./ContactFieldsEditor";
import {
  CONTACT_FIELDS_BRANCH_ACTION,
  DEFAULT_CONTACT_FIELDS_VALUE,
  isContactFieldsBranchAction,
  isContactFieldsValueValid,
  buildContactFieldsConfig,
  applyContactFieldsBranches,
} from "./contactFieldsBranch";
import SetContactFieldsEditor from "./SetContactFieldsEditor";
import {
  SET_CONTACT_FIELDS_ACTION,
  DEFAULT_SET_CONTACT_FIELDS_VALUE,
  isSetContactFieldsAction,
  getSetContactFieldsError,
  buildSetContactFieldsConfig,
} from "./setContactFieldsAction";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import toast from "react-hot-toast";
import { validateInstagramImageFile, validateInstagramMediaTemplateFile, validateInstagramFile } from "../../utils/instagramMediaValidation";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const MESSENGER_COLOR = "#0084FF";
const CATEGORY_ICONS = {
  Message: <MessageIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  Contact: <PersonIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
  "Quick Actions": <BuildCircleIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />,
};

// Facebook actions: same as Instagram (Send text, image, video, audio, templates, Assign conversation, Go to action); backend routes by contact.platform
const FACEBOOK_ACTIONS = [
  {
    name: "Send text message",
    description: "Send text message to a conversation",
    category: "Message",
    config: [
      {
        name: "Text Message Node",
        required: true,
        fields: [{ label: "Message", type: "text large" }],
      },
    ],
  },
  {
    name: "AI Reply",
    description: "Reply to the contact via DM using AI (RAG). When AI can't answer or fails, send fallback message and/or assign to a team member.",
    category: "Message",
    config: [
      {
        name: "AI Reply Node",
        required: true,
        fields: [
          {
            label: "AI Agent",
            type: "agent",
            required: false,
          },
          {
            label: "Fallback message (when AI can't answer)",
            type: "text large",
            required: false,
            placeholder: "e.g. I'm not sure about that. A team member will help you shortly.",
          },
          {
            label: "Assign to team member (when AI can't answer)",
            type: "teammember",
            required: false,
          },
        ],
      },
    ],
  },
  {
    name: "Send image message",
    description: "Send image message to a conversation",
    category: "Message",
    config: [
      {
        name: "Image Message Node",
        required: true,
        fields: [
          { label: "Image", type: "file", required: true, accept: "image/*" },
        ],
      },
    ],
  },
  {
    name: "Send video message",
    description: "Send video message to a conversation",
    category: "Message",
    config: [
      {
        name: "Video Message Node",
        required: true,
        fields: [
          { label: "Video", type: "file", required: true, accept: "video/*" },
        ],
      },
    ],
  },
  {
    name: "Send audio message",
    description: "Send audio message to a conversation",
    category: "Message",
    config: [
      {
        name: "Audio Message Node",
        required: true,
        fields: [
          { label: "Audio", type: "file", required: true, accept: "audio/*" },
        ],
      },
    ],
  },
  {
    name: "Send document message",
    description: "Send document message to a conversation (PDF)",
    category: "Message",
    config: [
      {
        name: "Document Message Node",
        required: true,
        fields: [
          { label: "Document", type: "file", required: true, accept: "application/pdf,.pdf" },
        ],
      },
    ],
  },
  {
    name: "Send button template message",
    description: "Send a message with up to 3 buttons (postback, link, or call)",
    category: "Message",
    config: [
      {
        name: "Button Template Node",
        required: true,
        fields: [
          { label: "Message text", type: "text large", required: true },
          {
            label: "Button",
            type: "section",
            subfields: [
              { label: "Button type", type: "select", options: [
                { label: "Postback", value: "postback" },
                { label: "Open URL", value: "web_url" },
              ]},
              { label: "Button title", type: "text", required: true },
              { label: "Payload or URL", type: "text", required: true },
            ],
          },
        ],
      },
    ],
  },
  {
    name: "Send generic template message",
    description: "Send a carousel of cards (image, title, subtitle, buttons)",
    category: "Message",
    config: [
      {
        name: "Generic Template Node",
        required: true,
        fields: [
          {
            label: "Card",
            type: "section",
            subfields: [
              { label: "Title", type: "text", required: true },
              { label: "Subtitle", type: "text" },
              { label: "Default action URL", type: "text", placeholder: "Optional: opens when card is tapped" },
              { label: "Image URL", type: "file", accept: "image/*" },
              { label: "Button title", type: "text" },
              { label: "Button URL or payload", type: "text" },
            ],
          },
        ],
      },
    ],
  },
  {
    name: "Send media template message",
    description: "Send an image or video with optional buttons",
    category: "Message",
    config: [
      {
        name: "Media Template Node",
        required: true,
        fields: [
          { label: "Media type", type: "select", options: [
            { label: "Image", value: "image" },
            { label: "Video", value: "video" },
          ]},
          { label: "Media URL", type: "file", required: true, accept: "image/*,video/*" },
          {
            label: "Button",
            type: "section",
            subfields: [
              { label: "Button title", type: "text", required: true },
              { label: "Button type", type: "select", options: [
                { label: "Open URL", value: "web_url" },
                { label: "Postback", value: "postback" },
              ]},
              { label: "URL or payload", type: "text", required: true },
            ],
          },
        ],
      },
    ],
  },
  {
    name: "Send product template message",
    description: "Send product(s) from catalog (show all, send selected) - Instagram product template",
    category: "Message",
    config: [
      {
        name: "Product Template Node",
        required: true,
        fields: [
          { label: "Select catalog & products", type: "catalog", required: true },
        ],
      },
    ],
  },
  {
    name: "Assign conversation",
    description: "Assign conversation to a member",
    category: "Contact",
    config: [
      {
        name: "Assign conversation to a member either directly or using round robin",
        required: true,
        fields: [
          {
            label: "Select Assignment Style",
            type: "select",
            options: [
              { label: "Round Robin (All)", pointer: null },
              {
                label: "Round Robin (specific members)",
                subfields: [
                  {
                    label: "Select member",
                    type: "multipleteammember",
                    options: "members",
                  },
                ],
                pointer: null,
              },
              {
                label: "Specific member",
                subfields: [
                  {
                    label: "Select member",
                    type: "teammember",
                    options: "members",
                  },
                ],
              },
              {
                label: "Specific team",
                subfields: [
                  {
                    label: "Select team",
                    type: "team",
                    options: "team",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: "Auto-close after assignment",
        required: false,
        fields: [
          { label: "Enable auto-close", type: "checkbox", selectedValue: false },
          {
            label: "Select Interval Duration",
            type: "number",
            selectedValue: 30,
            dependsOn: "Enable auto-close",
          },
          {
            label: "Select Duration Type",
            type: "select",
            selectedValue: "Minutes",
            options: ["Minutes", "Hours", "Days"],
            dependsOn: "Enable auto-close",
          },
        ],
      },
    ],
  },
  BUSINESS_HOURS_BRANCH_ACTION,
  CONTACT_FIELDS_BRANCH_ACTION,
  SET_CONTACT_FIELDS_ACTION,
  {
    name: "Go to action",
    description: "Go back to a specific action",
    category: "Quick Actions",
    config: [
      {
        name: "Go to",
        required: true,
        fields: [{ label: "Existing Actions", type: "actions" }],
      },
    ],
  },
];

// Group by category for accordion
const FACEBOOK_ACTIONS_BY_CATEGORY = ["Message", "Contact", "Logic", "Quick Actions"].map(
  (category) => ({
    category,
    actions: FACEBOOK_ACTIONS.filter((a) => a.category === category),
  })
);

// Meta's Private Reply API (recipient.comment_id) — the only way to send the first message
// after a Facebook comment, since a comment never opens the standard messaging window on its
// own — only supports a plain text message or a single image/video/audio/document attachment.
// These action types build a "template" attachment (or quick_replies), which Private Reply has
// no equivalent for, so they can't be the first action right after a "Comment on post" trigger.
const COMMENT_TRIGGER_INCOMPATIBLE_ACTIONS = new Set([
  "Send button template message",
  "Send generic template message",
  "Send media template message",
  "Send product template message",
]);
const COMMENT_TRIGGER_BLOCKED_TOOLTIP =
  "Not available as the first reply to a comment — Meta's Private Reply API only supports a text message or a single image/video/audio/document as the opening reply. Add a \"Send text message\" or media action first, then use this one later in the flow.";

const ChooseFacebookActionDrawer = ({
  open,
  onClose,
  editAction,
  editActionBranch,
  editActionNodeId,
  pendingInsert,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { selectedWorkflow } = useSelector((state) => state.workflow);
  const { teams, teamMembers, loading: settingsLoading } = useSelector((state) => state.settings);
  const { catalogs, status: catalogsStatus } = useSelector((state) => state.catalog);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const workspaceList = useSelector((state) => state.workspace?.workspaces);
  const authWorkspaceId = useSelector((state) => state.auth?.user?.workspaceId);
  const workspaceId =
    (selectedWorkflow?.workspaceId ? String(selectedWorkflow.workspaceId) : null) ||
    currentWorkspace?._id ||
    selectedWorkflow?.workspace?._id ||
    authWorkspaceId ||
    (Array.isArray(workspaceList) ? workspaceList[0]?._id : null) ||
    null;
  const catalogsFetchAttemptedRef = useRef(false);
  const catalogsFetchInProgressRef = useRef(false);
  const drawerBodyScrollRef = useRef(null);

  const [aiAgents, setAiAgents] = useState([]);
  const [aiAgentsLoading, setAiAgentsLoading] = useState(false);
  const aiAgentsCacheRef = useRef(new Map());

  useEffect(() => {
    if (!open) return;
    if (!workspaceId) {
      setAiAgents([]);
      setAiAgentsLoading(false);
      return;
    }
    if (aiAgentsCacheRef.current.has(workspaceId)) {
      setAiAgents(aiAgentsCacheRef.current.get(workspaceId) || []);
      setAiAgentsLoading(false);
      return;
    }

    let cancelled = false;
    setAiAgentsLoading(true);

    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled || !token) return;

        const r = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        if (!r.ok) {
          console.warn(`[AI Reply] Failed to load agents — HTTP ${r.status} for workspace ${workspaceId}`);
          return;
        }

        const data = await r.json();
        if (cancelled) return;

        const rawAgents = Array.isArray(data)
          ? data
          : Array.isArray(data?.agents)
            ? data.agents
            : Array.isArray(data?.data?.agents)
              ? data.data.agents
              : [];
        const agents = rawAgents.filter((a) => a?.isActive !== false);
        aiAgentsCacheRef.current.set(workspaceId, agents);
        setAiAgents(agents);
      } catch (err) {
        if (!cancelled) {
          console.error(`[AI Reply] Error loading agents for workspace ${workspaceId}:`, err?.message || err);
        }
      } finally {
        setAiAgentsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceId, open]);

  const [expanded, setExpanded] = useState("Message");
  const [selectedAction, setSelectedAction] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [localConfig, setLocalConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [businessHoursExpandedDay, setBusinessHoursExpandedDay] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [waitForKeyword, setWaitForKeyword] = useState(false);
  const [incorrectKeywordMessage, setIncorrectKeywordMessage] = useState({
    enabled: false,
    text: "I didn't understand that. Please try again.",
    showOptions: false,
  });
  const [actionSelectionMode, setActionSelectionMode] = useState(false);
  const [actionSelectionFieldKey, setActionSelectionFieldKey] = useState(null);
  const [keywordTargetSelectionMode, setKeywordTargetSelectionMode] = useState(false);
  const [keywordTargetSelectionIndex, setKeywordTargetSelectionIndex] = useState(null);
  const [mediaLibraryList, setMediaLibraryList] = useState([]);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);

  const branches = useMemo(
    () => selectedWorkflow?.currentVersion?.branches || {},
    [selectedWorkflow]
  );

  const extractWorkflowActions = useCallback(() => {
    const list = [];
    const currentBranchId = editActionBranch || pendingInsert?.branchId;
    Object.keys(branches).forEach((branchId) => {
      const branch = branches[branchId];
      if (!Array.isArray(branch)) return;
      branch.forEach((node, nodeIndex) => {
        if (node.type === "action" && node.action) {
          if (branchId === currentBranchId) return;
          list.push({
            id: `${branchId}-${nodeIndex}`,
            branchId,
            nodeId: node.id ?? nodeIndex,
            nodeIndex,
            name: node.action.name,
            description: node.action.description,
            category: node.action.category,
            action: node.action,
          });
        }
      });
    });
    return list;
  }, [branches, editActionBranch, pendingInsert]);

  const workflowActions = useMemo(
    () => extractWorkflowActions(),
    [extractWorkflowActions]
  );

  const extractWorkflowMessages = useMemo(() => {
    const messages = [];
    const getText = (action) => {
      if (!action?.config) return null;
      for (const cfg of action.config) {
        if (!cfg.fields) continue;
        for (const f of cfg.fields) {
          if ((f.type === "text large" || f.type === "text") && f.selectedValue)
            return String(f.selectedValue).trim();
          if (f.label && (f.label.toLowerCase().includes("message") || f.label === "Caption") && f.selectedValue)
            return String(f.selectedValue).trim();
        }
      }
      return null;
    };
    Object.keys(branches).forEach((branchId) => {
      const branch = branches[branchId];
      if (!Array.isArray(branch)) return;
      branch.forEach((node, nodeIndex) => {
        if (node.type === "action" && node.action && node.id) {
          const text = getText(node.action);
          messages.push({
            id: node.id,
            messageId: node.id,
            name: text ? (text.length > 60 ? text.slice(0, 57) + "..." : text) : node.action.name,
            actionName: node.action.name,
            messageText: text,
            branchId,
            nodeIndex,
          });
        }
      });
    });
    return messages;
  }, [branches]);

  const currentBranchId = editActionBranch || pendingInsert?.branchId;
  const currentBranch = currentBranchId ? branches[currentBranchId] : [];
  const currentNodeIndex = editActionNodeId !== undefined && editActionNodeId !== null
    ? currentBranch.findIndex(
        (n) =>
          n.id === editActionNodeId ||
          n._id === editActionNodeId ||
          String(n.id) === String(editActionNodeId)
      )
    : pendingInsert?.nodeIndex ?? -1;
  const isLastNode =
    currentBranch.length === 0 ||
    currentNodeIndex === currentBranch.length - 1 ||
    (pendingInsert && pendingInsert.nodeIndex >= currentBranch.length);
  const isNodeBeforeEndNode = isLastNode;

  // True when this drawer is choosing/editing the very first action right after a
  // "Comment on post" trigger — that message must go out via Meta's Private Reply API,
  // which only supports text or a single media attachment (see COMMENT_TRIGGER_INCOMPATIBLE_ACTIONS).
  const isCommentTriggerFirstSlot =
    currentBranchId === "start" &&
    currentNodeIndex === 1 &&
    branches?.start?.[0]?.trigger?.name === "Comment on post";

  // Function to handle action selection from the workflow
  const handleActionSelection = useCallback((selectedActionData) => {
    if (actionSelectionMode && actionSelectionFieldKey) {
      // Create a unique identifier for this action in the workflow
      const id = `${selectedActionData.branchId}-${selectedActionData.nodeIndex}`;

      // Update the form value
      setFormValues(v => ({ ...v, [actionSelectionFieldKey]: id }));

      // Exit selection mode
      setActionSelectionMode(false);
      setActionSelectionFieldKey(null);
    }
  }, [actionSelectionMode, actionSelectionFieldKey]);

  // Function to handle keyword target message selection from the workflow
  const handleKeywordTargetSelection = useCallback((selectedActionData) => {
    if (keywordTargetSelectionMode && keywordTargetSelectionIndex !== null) {
      const state = window.store?.getState?.();
      const br = state?.workflow?.selectedWorkflow?.currentVersion?.branches ?? {};
      const branch = br[selectedActionData.branchId];
      const node = branch?.[selectedActionData.nodeIndex];
      const targetId = node?.id ?? null;

      if (targetId != null && keywordTargetSelectionIndex !== null) {
        setKeywords((prev) => {
          const newKw = [...prev];
          if (!newKw[keywordTargetSelectionIndex]) newKw[keywordTargetSelectionIndex] = { keyword: "", targetId: "" };
          newKw[keywordTargetSelectionIndex] = { ...newKw[keywordTargetSelectionIndex], targetId };
          return newKw;
        });
      }
      
      // Exit selection mode
      setKeywordTargetSelectionMode(false);
      setKeywordTargetSelectionIndex(null);
    }
  }, [keywordTargetSelectionMode, keywordTargetSelectionIndex]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.handleActionSelection = handleActionSelection;
      window.setActionSelectionMode = setActionSelectionMode;
      window.setActionSelectionFieldKey = setActionSelectionFieldKey;
      window.isActionSelectionMode = () => actionSelectionMode;
      window.getCurrentSelectionBranchId = () => currentBranchId;
      
      // Expose keyword target selection handler
      window.handleKeywordTargetSelection = handleKeywordTargetSelection;
      window.isKeywordTargetSelectionMode = () => keywordTargetSelectionMode;
      
      return () => {
        window.handleActionSelection = null;
        window.setActionSelectionMode = null;
        window.setActionSelectionFieldKey = null;
        window.isActionSelectionMode = null;
        window.getCurrentSelectionBranchId = null;
        window.handleKeywordTargetSelection = null;
        window.isKeywordTargetSelectionMode = null;
      };
    }
  }, [
    actionSelectionMode,
    actionSelectionFieldKey,
    keywordTargetSelectionIndex,
    keywordTargetSelectionMode,
    keywords,
    currentBranchId,
    branches,
    handleActionSelection,
    handleKeywordTargetSelection,
  ]);

  useEffect(() => {
    if (!open || !editAction) return;
    setSelectedAction(editAction);
    setLocalConfig(editAction.config ? JSON.parse(JSON.stringify(editAction.config)) : null);
    const fv = {};
    const extract = (fields, prefix) => {
      (fields || []).forEach((field, i) => {
        const k = `${prefix}${field.label || field.name || i}`;
        // For all section fields (List item, Button, etc.), use index in prefix so keys match render
        if (field.type === 'section') {
          if (field.subfields) extract(field.subfields, `${k}-${i}-`);
          if (field.fields) extract(field.fields, `${k}-${i}-`);
          return; // Don't store section value itself
        }
        if (field.selectedValue !== undefined && field.type !== "actions") {
          if (field.type === "file" && typeof field.selectedValue === "object" && field.selectedValue && field.selectedValue.url) {
            fv[k] = { ...field.selectedValue, status: "uploaded", originalName: field.selectedValue.originalName || "Media" };
          } else if (field.type === "file" && typeof field.selectedValue === "string" && field.selectedValue.trim()) {
            const lbl = (field.label || "").toLowerCase();
            const originalName = lbl.includes("video") ? "Video" : lbl.includes("audio") ? "Audio" : lbl.includes("document") ? "Document" : "Image";
            fv[k] = { url: field.selectedValue.trim(), status: "uploaded", originalName };
          } else if (field.label === "Button type" && field.selectedValue === "phone_number") {
            fv[k] = "postback";
          } else {
            fv[k] = field.selectedValue;
          }
        }
        if (field.type === "actions" && field.selectedValue?.branchId != null)
          fv[k] = `${field.selectedValue.branchId}-${field.selectedValue.nodeIndex}`;
        if (field.subfields) extract(field.subfields, `${k}-`);
        if (field.fields) extract(field.fields, `${k}-`);
        if (field.type === "select" && field.options) {
          const sel = field.selectedValue ?? field.options[0]?.label;
          if (sel) {
            fv[k] = typeof sel === "object" ? sel.label : sel;
            const opt = field.options.find((o) => (o.label || o.value) === (typeof sel === "object" ? sel.label : sel));
            if (opt?.subfields) extract(opt.subfields, `${k}-`);
          }
        }
      });
    };
    (editAction.config || []).forEach((cfg, idx) => extract(cfg.fields || [], `${idx}-`));
    setFormValues(fv);
    if (editAction.category !== "Contact" && !String(editAction.name || "").toLowerCase().includes("go to")) {
      setKeywords(Array.isArray(editAction.keywords) ? editAction.keywords : []);
      setIncorrectKeywordMessage(
        editAction.incorrectKeywordMessage
          ? {
              enabled: !!editAction.incorrectKeywordMessage.enabled,
              text: editAction.incorrectKeywordMessage.text || "I didn't understand that. Please try again.",
              showOptions: !!editAction.incorrectKeywordMessage.showOptions,
            }
          : { enabled: false, text: "I didn't understand that. Please try again.", showOptions: false }
      );
      setWaitForKeyword(!!editAction.waitForKeyword);
    } else {
      setKeywords([]);
      setWaitForKeyword(false);
      setIncorrectKeywordMessage({ enabled: false, text: "I didn't understand that. Please try again.", showOptions: false });
    }
  }, [open, editAction]);

  useEffect(() => {
    if (selectedAction)
      setLocalConfig(selectedAction.config ? JSON.parse(JSON.stringify(selectedAction.config)) : null);
  }, [selectedAction]);

  useEffect(() => {
    if (!open) return;
    const el = drawerBodyScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [open, selectedAction, editAction, editActionNodeId, editActionBranch, pendingInsert]);

  useEffect(() => {
    if (open && (selectedAction?.name === "Assign conversation" || localConfig)) {
      if (!teams?.length) dispatch(fetchTeams());
      if (!teamMembers?.length) dispatch(fetchTeamMembers());
    }
  }, [open, selectedAction?.name, localConfig, teams?.length, teamMembers?.length, dispatch]);

  const needsCatalogs = useMemo(() => {
    if (!localConfig || !Array.isArray(localConfig)) return false;
    const check = (fields) => {
      for (const f of fields || []) {
        if (f.type === "catalog") return true;
        if (f.subfields && check(f.subfields)) return true;
        if (f.fields && check(f.fields)) return true;
      }
      return false;
    };
    for (const cfg of localConfig) {
      if (check(cfg.fields)) return true;
    }
    return false;
  }, [localConfig]);

  useEffect(() => {
    if (!open) {
      catalogsFetchAttemptedRef.current = false;
      catalogsFetchInProgressRef.current = false;
      return;
    }
    if (!needsCatalogs) return;
    if (
      !catalogsFetchAttemptedRef.current &&
      !catalogsFetchInProgressRef.current &&
      catalogsStatus !== "loading" &&
      catalogsStatus !== "succeeded" &&
      (!catalogs || catalogs.length === 0)
    ) {
      catalogsFetchAttemptedRef.current = true;
      catalogsFetchInProgressRef.current = true;
      dispatch(fetchCatalogsFromDb('facebook')).finally(() => {
        catalogsFetchInProgressRef.current = false;
      });
    }
    if (catalogsStatus === "succeeded" || catalogsStatus === "failed") {
      catalogsFetchInProgressRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- catalogs.length used intentionally instead of catalogs to prevent re-triggers
  }, [open, needsCatalogs, dispatch, catalogsStatus, catalogs?.length]);

  useEffect(() => {
    if (!open || selectedAction?.name !== "Send media template message") {
      setMediaLibraryList([]);
      return;
    }
    let cancelled = false;
    const fetchLibrary = async () => {
      setMediaLibraryLoading(true);
      try {
        const token = await getAuthToken();
        if (!token) return;
        const res = await fetch(`${SERVER_DOMAIN}/api/templates/media-library?platform=facebook`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data.items) setMediaLibraryList(data.items);
      } catch (e) {
        if (!cancelled) setMediaLibraryList([]);
      } finally {
        if (!cancelled) setMediaLibraryLoading(false);
      }
    };
    fetchLibrary();
    return () => { cancelled = true; };
  }, [open, selectedAction?.name]);

  function setSelectedValues(fields, formValues, parentKey = "", isBeforeEndNode = false) {
    return (fields || []).map((field, idx) => {
      const key = `${parentKey}${field.label || field.name || idx}`;
      let updated = { ...field };
      if (field.subfields)
        updated.subfields = setSelectedValues(field.subfields, formValues, `${key}-${idx}-`, isBeforeEndNode);
      if (field.fields)
        updated.fields = setSelectedValues(field.fields, formValues, `${key}-${idx}-`, isBeforeEndNode);
      if (field.type === "select" && Array.isArray(field.options)) {
        const sel = formValues[key];
        if (sel) {
          const opt = field.options.find((o) => (o.label || o.value) === sel);
          if (opt?.subfields)
            updated.options = field.options.map((o) =>
              (o.label || o.value) === sel && o.subfields
                ? { ...o, subfields: setSelectedValues(o.subfields, formValues, `${key}-`, isBeforeEndNode) }
                : o
            );
        }
      }
      if (field.label || field.name) {
        if (field.type === "actions") {
          if (isBeforeEndNode && formValues[key]) {
            const wf = workflowActions.find((a) => a.id === formValues[key]);
            updated.selectedValue = wf
              ? { branchId: wf.branchId, nodeIndex: wf.nodeIndex, actionId: wf.nodeId ?? wf.nodeIndex }
              : "";
          } else updated.selectedValue = "";
        } else if (field.type === "agent") {
          // Store the selected agent ID (empty string = use workspace default agent)
          updated.selectedValue = formValues[key] !== undefined ? formValues[key] : "";
        } else if (field.type === "file" && formValues[key] && typeof formValues[key] === "object" && formValues[key].url) {
          // Save full file object so reopen shows real file name and size (Instagram only)
          if (field.label === "Media URL" && formValues[key].attachment_id) {
            updated.selectedValue = {
              url: formValues[key].url,
              attachment_id: formValues[key].attachment_id,
              ...(formValues[key].uploaded_at != null ? { uploaded_at: formValues[key].uploaded_at } : {}),
              originalName: formValues[key].originalName,
              size: formValues[key].size,
            };
          } else {
            updated.selectedValue = {
              url: formValues[key].url,
              originalName: formValues[key].originalName,
              size: formValues[key].size,
            };
          }
        } else if (field.type === 'section' && field.label && field.label.toLowerCase().includes('button')) {
          // Button sections don't have selectedValue themselves, only their subfields do
          updated.selectedValue = undefined;
        } else {
          updated.selectedValue = formValues[key] !== undefined ? formValues[key] : "";
        }
      }
      return updated;
    });
  }

  // Helper function to count buttons in interactive reply buttons message
  const countButtons = (fields) => {
    let buttonCount = 0;
    
    if (!Array.isArray(fields)) return { buttonCount: 0 };
    
    for (const field of fields) {
      const fieldLabel = (field.label || '').toLowerCase();
      
      // Count "Button" fields (section type)
      if (fieldLabel === 'button' && Array.isArray(field.subfields)) {
        buttonCount++;
      }
    }
    
    return { buttonCount };
  };

  // Handler to duplicate a section at any depth
  const handleDuplicateSection = (cfgIdx, fieldPath) => {
    // For quick reply and button-template actions, validate max 3 buttons (Meta limit)
    const isButtonLimitAction =
      selectedAction?.name === "Send interactive reply buttons message" ||
      selectedAction?.name === "Send button template message";
    if (isButtonLimitAction) {
      let totalButtons = 0;
      
      if (localConfig && localConfig[cfgIdx] && Array.isArray(localConfig[cfgIdx].fields)) {
        const counts = countButtons(localConfig[cfgIdx].fields);
        totalButtons = counts.buttonCount;
      }
      
      // Check if we're trying to duplicate a button
      let currentFields = localConfig?.[cfgIdx]?.fields;
      let fieldToDuplicate = null;
      
      if (currentFields && fieldPath.length > 0) {
        for (let i = 0; i < fieldPath.length; i++) {
          const idx = fieldPath[i];
          if (currentFields && currentFields[idx]) {
            fieldToDuplicate = currentFields[idx];
            if (i < fieldPath.length - 1 && Array.isArray(fieldToDuplicate.subfields)) {
              currentFields = fieldToDuplicate.subfields;
            }
          }
        }
      }
      
      const isButtonField = fieldToDuplicate?.label?.toLowerCase() === 'button';
      
      if (isButtonField && totalButtons >= 3) {
        alert(
          selectedAction?.name === "Send button template message"
            ? "Maximum 3 buttons allowed for button templates (Meta limit)."
            : "Maximum 3 buttons allowed for Instagram quick replies."
        );
        return;
      }
    }
    
    setLocalConfig(prev => {
      if (!prev) return prev;
      const newConfig = [...prev];
      
      // Navigate to the parent of the field to duplicate
      let currentLevel = newConfig[cfgIdx].fields;
      const pathToParent = fieldPath.slice(0, -1);
      
      for (const idx of pathToParent) {
        if (currentLevel && currentLevel[idx] && Array.isArray(currentLevel[idx].subfields)) {
          currentLevel = currentLevel[idx].subfields;
        }
      }
      
      // Duplicate the field at the final index
      const finalIdx = fieldPath[fieldPath.length - 1];
      if (currentLevel && currentLevel[finalIdx]) {
        const fieldToDuplicate = JSON.parse(JSON.stringify(currentLevel[finalIdx]));
        // Clear selected values in the duplicated field
        const clearValues = (fields) => {
          if (!Array.isArray(fields)) return;
          fields.forEach(f => {
            if (f.selectedValue !== undefined) f.selectedValue = '';
            if (f.subfields) clearValues(f.subfields);
            if (f.fields) clearValues(f.fields);
          });
        };
        clearValues([fieldToDuplicate]);
        currentLevel.splice(finalIdx + 1, 0, fieldToDuplicate);
      }
      
      return newConfig;
    });
  };

  // Handler to remove a section at any depth
  function removeSectionAtPath(fields, path) {
    if (path.length === 0) return fields;
    const idx = path[0];
    if (path.length === 1) {
      // Remove at this level
      const newFields = [...fields];
      newFields.splice(idx, 1);
      return newFields;
    } else {
      // Go deeper
      return fields.map((f, i) => {
        if (i === idx && Array.isArray(f.subfields)) {
          return { ...f, subfields: removeSectionAtPath(f.subfields, path.slice(1)) };
        }
        return f;
      });
    }
  }

  const handleRemoveSection = (cfgIdx, fieldPath) => {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const newConfig = [...prev];
      const fields = newConfig[cfgIdx].fields;
      const newFields = removeSectionAtPath(fields, fieldPath);
      newConfig[cfgIdx] = { ...newConfig[cfgIdx], fields: newFields };
      return newConfig;
    });
  };

  const handleSaveAction = async () => {
    if (!selectedAction) return;
    const configWithSelectedValues = (localConfig || []).map((cfg, idx) => ({
      ...cfg,
      fields: setSelectedValues(cfg.fields || [], formValues, `${idx}-`, isNodeBeforeEndNode),
    }));

    if (selectedAction.name === "Assign conversation") {
      const assignmentField = configWithSelectedValues[0]?.fields?.find(
        (f) => f.label === "Select Assignment Style"
      );
      if (assignmentField?.selectedValue) {
        const opt = assignmentField.options?.find((o) => o.label === assignmentField.selectedValue);
        opt?.subfields?.forEach((sub) => {
          const formKey = Object.keys(formValues).find((k) => k.includes(sub.label));
          if (formKey) {
            const val = formValues[formKey];
            if (sub.type === "teammember" || sub.type === "multipleteammember") {
              const selectedIds = Array.isArray(val) ? val : [val];
              sub.selectedValues = selectedIds;
              sub.selectedNames = selectedIds.map(id => {
                const member = (teamMembers || []).find(m => m._id === id || String(m._id) === String(id));
                return member ? (member.name || member.email || id) : id;
              });
            } else if (sub.type === "team") {
              sub.selectedValues = [val];
              const team = (teams || []).find(t => t._id === val || String(t._id) === String(val));
              sub.selectedNames = [team ? (team.teamName || team.name || val) : val];
            }
          }
        });
      }
    }

    const actionWithConfig = { ...selectedAction, config: configWithSelectedValues };

    if (isBusinessHoursBranchAction(selectedAction.name)) {
      const configValue = formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE;
      if (!isBusinessHoursValueValid(configValue)) {
        alert("Fix the schedule errors before saving (each enabled day needs at least one valid, non-overlapping interval).");
        return;
      }
      actionWithConfig.config = buildBusinessHoursConfig(configValue);
      await applyBusinessHoursBranches({ dispatch, editAction, actionWithConfig, configValue });
    }

    if (isSetContactFieldsAction(selectedAction.name)) {
      const configValue = formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_SET_CONTACT_FIELDS_VALUE;
      const setFieldsError = getSetContactFieldsError(configValue);
      if (setFieldsError) {
        alert(setFieldsError);
        return;
      }
      actionWithConfig.config = buildSetContactFieldsConfig(configValue);
    }

    if (isContactFieldsBranchAction(selectedAction.name)) {
      const configValue = formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE;
      if (!isContactFieldsValueValid(configValue)) {
        alert("Select at least one contact field.");
        return;
      }
      actionWithConfig.config = buildContactFieldsConfig(configValue);
      await applyContactFieldsBranches({ dispatch, editAction, actionWithConfig, configValue });
    }

    // Validate interactive reply buttons message (Instagram)
    if (selectedAction.name === 'Send interactive reply buttons message') {
      // Validate buttons count
      let totalButtons = 0;
      
      (actionWithConfig.config || []).forEach(cfg => {
        if (Array.isArray(cfg.fields)) {
          const counts = countButtons(cfg.fields);
          totalButtons += counts.buttonCount;
        }
      });
      
      // Validate at least one button exists
      if (totalButtons === 0) {
        alert('At least one button is required. Please add at least one button before saving.');
        return;
      }
      
      // Validate button count (max 3 for Instagram)
      if (totalButtons > 3) {
        alert(`Maximum 3 buttons allowed for Instagram. You have ${totalButtons} buttons. Please remove ${totalButtons - 3} button(s) before saving.`);
        return;
      }
      
      // Validate each button has a title
      const buttons = [];
      (actionWithConfig.config || []).forEach(cfg => {
        (cfg.fields || []).forEach(field => {
          if (field.label && field.label.toLowerCase().includes('button') && Array.isArray(field.subfields)) {
            const titleField = field.subfields.find(f => 
              f.label === 'Button Title' && f.selectedValue
            );
            if (titleField && titleField.selectedValue && titleField.selectedValue.trim()) {
              buttons.push({ title: titleField.selectedValue.trim() });
            }
          }
        });
      });
      
      if (buttons.length === 0) {
        alert('At least one button with a title is required. Please fill in button titles before saving.');
        return;
      }
      
      if (buttons.length !== totalButtons) {
        alert(`All buttons must have titles. ${totalButtons - buttons.length} button(s) are missing titles.`);
        return;
      }
    }

    // Button template (Meta: max 3 buttons; must match branches / server slice)
    if (selectedAction.name === "Send button template message") {
      let totalButtons = 0;
      (actionWithConfig.config || []).forEach((cfg) => {
        if (Array.isArray(cfg.fields)) {
          const counts = countButtons(cfg.fields);
          totalButtons += counts.buttonCount;
        }
      });
      if (totalButtons > 3) {
        alert(
          `Maximum 3 buttons allowed for button templates (Meta limit). You have ${totalButtons} buttons. Please remove ${totalButtons - 3} button(s) before saving.`
        );
        return;
      }
    }

    if (selectedAction.name === "Send product template message") {
      const findCatalogField = (fields) => {
        if (!Array.isArray(fields)) return null;
        for (const f of fields) {
          if (f.type === "catalog") return f;
          if (f.subfields) {
            const found = findCatalogField(f.subfields);
            if (found) return found;
          }
          if (f.fields) {
            const found = findCatalogField(f.fields);
            if (found) return found;
          }
        }
        return null;
      };
      let catalogField = null;
      for (const cfg of configWithSelectedValues || []) {
        catalogField = findCatalogField(cfg.fields);
        if (catalogField) break;
      }
      if (!catalogField) {
        alert("Product template requires a catalog field. Please check the action configuration.");
        return;
      }
      const catalogValue = catalogField.selectedValue;
      if (!catalogValue || typeof catalogValue !== "object" || (!catalogValue.id && !catalogValue._id)) {
        alert("Please select a catalog and at least one product before saving.");
        return;
      }
      if (!catalogValue.products || !Array.isArray(catalogValue.products) || catalogValue.products.length === 0) {
        alert("Please select at least one product from the catalog.");
        return;
      }
    }

    if (selectedAction.name === "Send media template message") {
      const mediaUrlKey = Object.keys(formValues).find((k) => k.includes("Media URL"));
      const mediaVal = mediaUrlKey ? formValues[mediaUrlKey] : null;
      const hasMedia = mediaVal && (typeof mediaVal === "object" ? mediaVal.url : String(mediaVal).trim());
      if (hasMedia) {
        const hasAttachmentId = typeof mediaVal === "object" && mediaVal.attachment_id;
        if (!hasAttachmentId) {
          alert("Media must be uploaded to Meta before saving. Please upload the file and wait for it to complete, then try again.");
          return;
        }
        const uploadedAt = typeof mediaVal === "object" && mediaVal.uploaded_at;
        if (uploadedAt) {
          const expiresAt = new Date(uploadedAt).getTime() + 90 * 24 * 60 * 60 * 1000;
          if (Date.now() > expiresAt) {
            alert("Media expired. Meta limits attachments to 90 days. Please re-upload the file and try again.");
            return;
          }
        }
      } else {
        alert("Please upload an image or video for the media template before saving.");
        return;
      }
    }

    // Send button template message: create one branch per button so workflow waits for user to tap before continuing
    if (selectedAction.name === "Send button template message") {
      const templateButtons = [];
      (actionWithConfig.config || []).forEach(cfg => {
        (cfg.fields || []).forEach(field => {
          if (field.label && field.label.toLowerCase().includes("button") && Array.isArray(field.subfields)) {
            const titleF = field.subfields.find(f => (f.label === "Button title" || f.label === "Button Title") && f.selectedValue);
            const payloadF = field.subfields.find(f => (f.label === "Payload or URL" || f.label === "Payload or url") && f.selectedValue);
            const typeF = field.subfields.find(f => (f.label === "Button type" || f.label === "Button Type") && f.selectedValue);
            const title = titleF && titleF.selectedValue ? String(titleF.selectedValue).trim() : "";
            const payloadOrUrl = payloadF && payloadF.selectedValue ? String(payloadF.selectedValue).trim() : "";
            const btnType = ((typeF && typeF.selectedValue) || "postback").toLowerCase();
            if (title) {
              templateButtons.push({ title, payload: payloadOrUrl || title, type: btnType });
            }
          }
        });
      });
      if (templateButtons.length > 0) {
        const currentState = window.store?.getState ? window.store.getState() : null;
        const currentBranches = currentState?.workflow?.selectedWorkflow?.currentVersion?.branches || {};
        let branchIds = [];
        if (editAction && editAction.next && Array.isArray(editAction.next)) {
          branchIds = editAction.next.filter(id => id.startsWith("button-"));
        }
        if (branchIds.length !== templateButtons.length) {
          branchIds = templateButtons.map((_, idx) => `button-${Date.now()}-${idx}`);
        }
        while (branchIds.length < templateButtons.length) {
          branchIds.push(`button-${Date.now()}-${branchIds.length}`);
        }
        if (branchIds.length > templateButtons.length) {
          const toRemove = branchIds.slice(templateButtons.length);
          branchIds = branchIds.slice(0, templateButtons.length);
          for (const bid of toRemove) {
            await dispatch(removeBranch({ branchId: bid }));
          }
        }
        for (let i = 0; i < branchIds.length; i++) {
          const branchId = branchIds[i];
          const btn = templateButtons[i];
          if (!currentBranches[branchId]) {
            await dispatch(addBranch({ branchId }));
          }
          const triggerNode = {
            id: `${branchId}-trigger`,
            type: "trigger",
            trigger: {
              name: "button reply",
              description: "Trigger for button template reply",
              triggerType: "non-root",
              config: [{
                fields: [
                  { label: "Button Title", type: "text", selectedValue: btn.title },
                  { label: "Payload", type: "text", selectedValue: btn.payload },
                ],
              }],
              category: "Nil",
            },
          };
          await dispatch(updateNodeInBranch({ branchId, nodeId: `${branchId}-trigger`, updates: triggerNode }));
          const stateAfter = window.store?.getState ? window.store.getState() : null;
          const branchAfter = stateAfter?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
          if (!branchAfter || branchAfter.length === 0) {
            await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
          }
        }
        actionWithConfig.next = branchIds;
      }
    }

    const isContact = selectedAction.category === "Contact" || isBusinessHoursBranchAction(selectedAction.name) || isContactFieldsBranchAction(selectedAction.name);
    const isGoTo = (selectedAction.name || "").toLowerCase().includes("go to");
    if (!isContact && !isGoTo) {
      if (keywords?.length) actionWithConfig.keywords = keywords.filter((k) => k.keyword && k.targetId);
      else delete actionWithConfig.keywords;
      actionWithConfig.incorrectKeywordMessage = incorrectKeywordMessage;
      if (keywords?.some((k) => k.keyword && k.targetId)) actionWithConfig.waitForKeyword = waitForKeyword;
      else delete actionWithConfig.waitForKeyword;
    } else {
      delete actionWithConfig.keywords;
      delete actionWithConfig.incorrectKeywordMessage;
      delete actionWithConfig.waitForKeyword;
    }
    actionWithConfig.next = actionWithConfig.next || [];

    setSaving(true);
    if (editActionBranch != null && editActionNodeId != null) {
      await dispatch(
        updateNodeInBranch({
          branchId: editActionBranch,
          nodeId: editActionNodeId,
          updates: { action: actionWithConfig, next: actionWithConfig.next },
        })
      );
      if (actionWithConfig.next?.length)
        await dispatch(
          setNodeNextBranches({
            branchId: editActionBranch,
            nodeId: editActionNodeId,
            next: actionWithConfig.next,
          })
        );
    } else if (pendingInsert?.branchId != null) {
      const node = {
        id: Date.now().toString(),
        type: "action",
        action: actionWithConfig,
        next: actionWithConfig.next,
      };
      await dispatch(
        addNodeToBranch({
          branchId: pendingInsert.branchId,
          node,
          nodeIndex: pendingInsert.nodeIndex,
        })
      );
      if (actionWithConfig.next?.length) {
        const state = window.store?.getState?.() ?? null;
        const branch = state?.workflow?.selectedWorkflow?.currentVersion?.branches?.[pendingInsert.branchId] ?? [];
        const added = branch.find((n) => n.action === actionWithConfig || n.id === node.id);
        if (added)
          await dispatch(
            setNodeNextBranches({
              branchId: pendingInsert.branchId,
              nodeId: added.id,
              next: actionWithConfig.next,
            })
          );
      }
    } else {
      setSaving(false);
      return;
    }

    const finalWorkflow = window.store?.getState?.()?.workflow?.selectedWorkflow;
    if (finalWorkflow?._id) {
      const token = await getAuthToken();
      if (!token) {
        setSaving(false);
        return;
      }
      try {
        await axios.put(
          `${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`,
          {
            currentVersion: {
              name: finalWorkflow.currentVersion?.name ?? "Untitled Workflow",
              description: finalWorkflow.currentVersion?.description ?? "",
              branches: finalWorkflow.currentVersion?.branches ?? { start: [] },
            },
            status: finalWorkflow.status ?? "off",
          },
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
        );
        setSaving(false);
        onClose?.();
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || err.message || "Failed to save workflow");
        setSaving(false);
      }
    } else setSaving(false);
  };

  const renderFields = (fields, parentKey = "", cfgIdx = 0, path = []) => {
    if (!Array.isArray(fields)) return null;
    
    // Count occurrences of each label for numbering
    const sectionCounts = {};
    fields.forEach(f => {
      if (f.type === 'section' && f.label) {
        const label = f.label || f.name || '';
        sectionCounts[label] = (sectionCounts[label] || 0) + 1;
      }
    });
    // Track the current index for each label
    const sectionIndex = {};
    
    // For button messages, calculate current button count to check limits
    let currentButtonCount = 0;
    let canAddButton = true;
    
    const isButtonLimitAction =
      selectedAction?.name === "Send interactive reply buttons message" ||
      selectedAction?.name === "Send button template message";
    if (isButtonLimitAction &&
        localConfig && localConfig[cfgIdx] && Array.isArray(localConfig[cfgIdx].fields)) {
      const counts = countButtons(localConfig[cfgIdx].fields);
      currentButtonCount = counts.buttonCount;
      canAddButton = currentButtonCount < 3;
    }
    
    return fields.map((field, idx) => {
      const key = `${parentKey}${field.label || field.name || idx}`;
      const thisPath = [...path, idx];
      const showField = field.dependsOn
        ? formValues[Object.keys(formValues).find((k) => k.includes("Enable auto-close"))]
        : true;
      if (field.dependsOn && !showField) return null;

      // Section type: render with +/- buttons for duplication/removal
      if (field.type === "section" && Array.isArray(field.subfields)) {
        const isTopLevel = path.length === 0;
        const label = field.label || field.name || '';
        let numberedLabel = label;
        if (sectionCounts[label] > 1) {
          sectionIndex[label] = (sectionIndex[label] || 0) + 1;
          numberedLabel = `${label} ${sectionIndex[label]}`;
        }
        // Show minus button for duplicated sections (index > 0)
        const showMinus = sectionCounts[label] > 1 && sectionIndex[label] > 1;
        
        const isButtonField = label.toLowerCase() === 'button';
        const shouldDisableAdd = isButtonField && !canAddButton;
        
        return (
          <Box
            key={key}
            sx={{
              mb: 0.5,
              ml: isTopLevel ? 0.5 : 1.5,
              borderLeft: `1px solid ${isDark ? '#555' : '#bbb'}`,
              pl: isTopLevel ? 1.5 : 2,
              minHeight: 32,
              position: 'relative',
              background: 'transparent',
              display: 'block',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.2 }}>
              <Typography
                sx={{
                  fontWeight: isTopLevel ? 700 : 500,
                  color: isDark ? '#fff' : '#222',
                  fontSize: isTopLevel ? 16 : 15,
                  letterSpacing: 0.1,
                  whiteSpace: 'nowrap',
                  display: 'block',
                  mr: 2,
                }}
              >
                {numberedLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDuplicateSection(cfgIdx, thisPath)}
                disabled={shouldDisableAdd}
                title={shouldDisableAdd ? 
                  (isButtonField ? 'Maximum 3 buttons allowed' : `Cannot add more ${label}`) : 
                  `Add ${label}`}
                sx={{
                  p: '2px',
                  color: shouldDisableAdd ? (isDark ? '#666' : '#999') : (isDark ? '#fff' : '#222'),
                  background: 'transparent',
                  '&:hover': { background: shouldDisableAdd ? 'transparent' : (isDark ? '#333' : '#eee') },
                  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                  ml: 0.5,
                }}>
                <span style={{ fontSize: 16, fontWeight: 700, display: 'inline-block', lineHeight: 1 }}>+</span>
              </IconButton>
              {showMinus && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveSection(cfgIdx, thisPath)}
                  sx={{
                    p: '2px',
                    color: isDark ? '#fff' : '#222',
                    background: 'transparent',
                    '&:hover': { background: isDark ? '#333' : '#eee' },
                    ml: 0.5,
                  }}>
                  <span style={{ fontSize: 16, fontWeight: 700, display: 'inline-block', lineHeight: 1 }}>-</span>
                </IconButton>
              )}
            </Box>
            <Box sx={{ width: '100%' }}>{renderFields(field.subfields, `${key}-${idx}-`, cfgIdx, thisPath)}</Box>
          </Box>
        );
      }

      if (field.type === "text large" || field.type === "text") {
        const isMessageField = field.label === "Message" && selectedAction?.name === "Send text message";
        const isButtonTemplateText = field.label === "Message text" && selectedAction?.name === "Send button template message";
        const maxLen = isMessageField ? 1000 : isButtonTemplateText ? 640 : undefined;
        const metaHelper = isMessageField ? "Meta: max 1000 characters" : isButtonTemplateText ? "Meta: max 640 characters" : "";
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <TextField
              fullWidth
              multiline={field.type === "text large"}
              minRows={field.type === "text large" ? 3 : 1}
              placeholder={field.placeholder ?? ""}
              helperText={field.helperText ?? metaHelper}
              inputProps={maxLen != null ? { maxLength: maxLen } : undefined}
              value={formValues[key] ?? ""}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: maxLen != null ? e.target.value.slice(0, maxLen) : e.target.value }))}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  color: isDark ? "#fff" : "#222",
                  "& fieldset": { borderColor: isDark ? "#555" : "#ccc" },
                },
              }}
            />
          </Box>
        );
      }
      if (field.type === "file") {
        const isMediaTemplateMedia = selectedAction?.name === "Send media template message" && field.label === "Media URL";
        const lbl = (field.label || "").toLowerCase();
        const accept = (field.accept || "").toLowerCase();
        // Use correct validator per type so video/audio/document don't get "image must be PNG" error
        const validateFile =
          isMediaTemplateMedia
            ? validateInstagramMediaTemplateFile
            : lbl.includes("video") || accept.includes("video") || lbl.includes("audio") || accept.includes("audio") || lbl.includes("document") || accept.includes("pdf")
            ? validateInstagramFile
            : validateInstagramImageFile;
        const formatSizeHint =
          lbl.includes("video") || accept.includes("video")
            ? "MP4, OGG, AVI, MOV, WebM (Max 25MB)"
            : lbl.includes("audio") || accept.includes("audio")
            ? "AAC, M4A, WAV, MP4 (Max 25MB)"
            : lbl.includes("document") || accept.includes("pdf")
            ? "PDF (Max 25MB)"
            : isMediaTemplateMedia
            ? "Image: PNG, JPEG, GIF (Max 8MB). Video: MP4, etc. (Max 25MB)"
            : "PNG, JPEG, GIF (Max 8MB)";
        const uploadedLabel =
          lbl.includes("video") ? "Video uploaded" : lbl.includes("audio") ? "Audio uploaded" : lbl.includes("document") ? "Document uploaded" : "Image uploaded";
        const getRemainingDays = (uploadedAt) => {
          const up = new Date(uploadedAt).getTime();
          const expiresAt = up + 90 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          if (now > expiresAt) return { expired: true, text: "Expired" };
          const days = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));
          return { expired: false, days, text: days <= 7 ? `Expires in ${days} day${days !== 1 ? "s" : ""}` : `Expires in ${days} days` };
        };
        const hasSelection = formValues[key] && typeof formValues[key] === "object" && formValues[key].attachment_id;
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            {isMediaTemplateMedia && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Choose from library or upload new</Typography>
                {mediaLibraryLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={18} /><Typography variant="caption">Loading library…</Typography></Box>
                ) : mediaLibraryList.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: 160,
                      overflowY: "auto",
                      border: "1px solid",
                      borderColor: isDark ? "#444" : "#e0e0e0",
                      borderRadius: 1,
                      p: 0.75,
                      scrollbarWidth: { xs: "none", sm: "auto" },
                      msOverflowStyle: { xs: "none", sm: "auto" },
                      "&::-webkit-scrollbar": {
                        width: { xs: 0, sm: 8 },
                        height: { xs: 0, sm: 8 },
                      },
                    }}
                  >
                    {mediaLibraryList.map((item) => {
                      const { expired, text } = getRemainingDays(item.uploaded_at);
                      const label = item.displayName || (item.mediaType === "video" ? "Video" : "Image");
                      const isSelected = !expired && formValues[key]?.attachment_id === item.attachment_id;
                      return (
                        <Box key={item._id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, px: 0.5, borderRadius: 0.5, bgcolor: isSelected ? "primary.light" : expired ? "action.hover" : "transparent", border: isSelected ? "1px solid" : "none", borderColor: "primary.main" }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={isSelected && !expired ? { color: (theme) => theme.palette.getContrastText(theme.palette.primary.light) } : undefined}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color: expired
                                  ? "error.main"
                                  : isSelected
                                    ? (theme) => theme.palette.getContrastText(theme.palette.primary.light)
                                    : "text.secondary",
                              }}
                            >
                              {text}
                            </Typography>
                          </Box>
                          <Button size="small" variant={isSelected ? "contained" : "outlined"} disabled={expired} onClick={() => setFormValues((v) => ({ ...v, [key]: { status: "uploaded", url: item.url, attachment_id: item.attachment_id, uploaded_at: item.uploaded_at, originalName: item.displayName || label, mediaType: item.mediaType || "image" } }))}>
                            {expired ? "Expired" : isSelected ? "Selected" : "Select"}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary">No library items yet. Upload below to add.</Typography>
                )}
              </Box>
            )}
            {!hasSelection && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = field.accept || "image/*";
                  input.onchange = async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const validation = validateFile(file);
                      if (!validation.valid) {
                        toast.error(validation.error);
                        return;
                      }
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("platform", "facebook");

                        const token = await getAuthToken();
                        if (!token) {
                          toast.error("No authentication token found");
                          return;
                        }

                        setFormValues((v) => ({ ...v, [key]: { status: "uploading", file: file } }));

                        const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
                        const uploadUrl = `${serverDomain}/api/templates/sendupload`;

                        const response = await fetch(uploadUrl, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });

                        if (!response.ok) {
                          const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
                          const msg = errorData.userFriendlyMessage || errorData.error || errorData.message || "Upload failed";
                          toast.error(msg);
                          setFormValues((v) => ({ ...v, [key]: "" }));
                          return;
                        }

                        const result = await response.json();
                        const fileUrl = result.data?.url || result.url || result.data;
                        if (!fileUrl) {
                          toast.error("No URL returned from upload");
                          setFormValues((v) => ({ ...v, [key]: "" }));
                          return;
                        }

                        if (isMediaTemplateMedia) {
                          const mediaTypeKey = Object.keys(formValues).find((k) => k.includes("Media type"));
                          const mediaType = (formValues[mediaTypeKey] || "image").toLowerCase();
                          try {
                            const attachRes = await fetch(`${serverDomain}/api/templates/facebook-attachment`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ url: fileUrl, mediaType: mediaType === "video" ? "video" : "image" }),
                            });
                            const attachData = await attachRes.json().catch(() => ({}));
                            const attachmentId = attachData.attachment_id;
                            setFormValues((v) => ({
                              ...v,
                              [key]: {
                                status: "uploaded",
                                url: fileUrl,
                                attachment_id: attachmentId,
                                ...(attachData.uploaded_at ? { uploaded_at: attachData.uploaded_at } : {}),
                                mediaType: mediaType === "video" ? "video" : "image",
                                originalName: file.name,
                                size: file.size,
                                type: file.type,
                              },
                            }));
                            if (!attachmentId) toast.error("Upload succeeded but attachment ID not received. Media template may fall back to URL.");
                          } catch (err) {
                            console.error("Facebook attachment upload error:", err);
                            toast.error("Media uploaded but failed to get attachment ID. You can still use the URL.");
                            setFormValues((v) => ({
                              ...v,
                              [key]: {
                                status: "uploaded",
                                url: fileUrl,
                                originalName: file.name,
                                size: file.size,
                                type: file.type,
                              },
                            }));
                          }
                        } else {
                          setFormValues((v) => ({
                            ...v,
                            [key]: {
                              status: "uploaded",
                              url: fileUrl,
                              originalName: file.name,
                              size: file.size,
                              type: file.type,
                            },
                          }));
                        }
                      } catch (error) {
                        console.error("Error uploading file:", error);
                        toast.error(error.message || "Failed to upload file.");
                        setFormValues((v) => ({ ...v, [key]: "" }));
                      }
                    }
                  };
                  input.click();
                }}
                startIcon={<CloudUploadIcon />}
                disabled={formValues[key]?.status === "uploading"}
              >
                {formValues[key]?.status === "uploading"
                  ? "Uploading..."
                  : field.label === "Video"
                  ? "Upload Video"
                  : field.label === "Audio"
                  ? "Upload Audio"
                  : field.label === "Document"
                  ? "Upload Document"
                  : "Upload Image"}
              </Button>
              <Typography variant="caption" color="textSecondary">
                {formatSizeHint}
              </Typography>
            </Box>
            )}
            {formValues[key] && typeof formValues[key] === "object" && (formValues[key].url || formValues[key].status === "uploaded") && (
              <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: isDark ? "#444" : "#e0e0e0", borderRadius: 1, background: isDark ? "#23272b" : "#f5f5f5" }}>
                {formValues[key].status === "uploading" ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="info.main">
                      Uploading...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                      <CheckIcon sx={{ color: "success.main", fontSize: 20 }} />
                      <Typography variant="body2" color="success.main">
                        {uploadedLabel}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setFormValues((v) => ({ ...v, [key]: "" }))}
                        aria-label="Remove file"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                      {isMediaTemplateMedia && (
                        <Button size="small" color="secondary" onClick={() => setFormValues((v) => ({ ...v, [key]: "" }))}>
                          Clear selection
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      File: {formValues[key].originalName || "Media"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Size: {formValues[key].size != null ? `${(formValues[key].size / 1024 / 1024).toFixed(2)} MB` : "—"}
                    </Typography>
                    {isMediaTemplateMedia && formValues[key].uploaded_at && (() => {
                      const uploadedAt = new Date(formValues[key].uploaded_at).getTime();
                      const expiresAt = uploadedAt + 90 * 24 * 60 * 60 * 1000;
                      const now = Date.now();
                      if (now > expiresAt) {
                        return <Typography variant="caption" color="error.main" display="block">Expired – please re-upload</Typography>;
                      }
                      const daysLeft = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));
                      if (daysLeft <= 7) {
                        return <Typography variant="caption" color="warning.main" display="block">Expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}</Typography>;
                      }
                      return <Typography variant="caption" color="textSecondary" display="block">Valid for 90 days (expires {new Date(expiresAt).toLocaleDateString()})</Typography>;
                    })()}
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      }
      if (field.type === "number") {
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <input
              type="number"
              value={formValues[key] ?? field.selectedValue ?? ""}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
              style={{
                width: "90%",
                marginTop: 4,
                padding: "8px 10px",
                borderRadius: 6,
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                background: isDark ? "#23272b" : "#fff",
                color: isDark ? "#fff" : "#222",
              }}
            />
          </Box>
        );
      }
      if (field.type === "checkbox") {
        return (
          <Box key={key} sx={{ mb: 2, ml: 2, display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={!!formValues[key]}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.checked }))}
              sx={{ p: 0.5, mr: 1 }}
            />
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
          </Box>
        );
      }
      if (field.type === "select" && Array.isArray(field.options)) {
        const selected = formValues[key] ?? (field.options[0]?.value ?? field.options[0]?.label ?? field.options[0]);
        const selectedLabel = typeof selected === "object" ? (selected?.value ?? selected?.label) : selected;
        const optionByMatch = field.options.find((o) => {
          if (typeof o === "string") return o === selectedLabel;
          return (o.value || o.label) === selectedLabel || o.label === selectedLabel;
        });
        const firstOpt = field.options[0];
        const defaultVal =
          typeof firstOpt === "string" ? firstOpt : (firstOpt?.value ?? firstOpt?.label ?? "");
        const safeValue = optionByMatch
          ? typeof optionByMatch === "string"
            ? optionByMatch
            : (optionByMatch.value ?? optionByMatch.label ?? "")
          : defaultVal;
        const selectedOption = field.options.find((o) => {
          const v = typeof o === "string" ? o : (o.value ?? o.label);
          return String(v) === String(safeValue);
        });
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <FormControl size="small" sx={{ mt: 0.5, width: "90%", maxWidth: "100%" }}>
              <Select
                value={String(safeValue)}
                onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
                MenuProps={{
                  disablePortal: false,
                  PaperProps: {
                    sx: {
                      zIndex: (t) => t.zIndex.modal + 2,
                      bgcolor: isDark ? "#2d3136" : "#fff",
                      maxHeight: 320,
                    },
                  },
                }}
                sx={{
                  borderRadius: "6px",
                  fontSize: 15,
                  bgcolor: isDark ? "#23272b" : "#fff",
                  color: isDark ? "#fff" : "#222",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDark ? "#444" : "#ccc",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDark ? "#666" : "#999",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: MESSENGER_COLOR,
                  },
                }}
              >
                {field.options.map((opt, i) => {
                  const val = typeof opt === "object" ? (opt.value ?? opt.label) : opt;
                  const label = typeof opt === "object" ? opt.label : opt;
                  return (
                    <MenuItem key={i} value={String(val)} sx={{ fontSize: 15 }}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedOption?.subfields && (
              <Box sx={{ ml: 2, mt: 1 }}>{renderFields(selectedOption.subfields, `${key}-`, cfgIdx, thisPath)}</Box>
            )}
          </Box>
        );
      }
      if (field.type === "actions") {
        if (!isNodeBeforeEndNode) {
          return (
            <Box key={key} sx={{ mb: 2, ml: 2 }}>
              <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
              <Box sx={{ mt: 1, p: 2, bgcolor: isDark ? "#2a2a2a" : "#f8f8f8", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: isDark ? "#bbb" : "#666", fontStyle: "italic" }}>
                  Go to action is only available at the end of a workflow branch.
                </Typography>
              </Box>
            </Box>
          );
        }
        const selectedId = formValues[key];
        const selectedWf = workflowActions.find((a) => a.id === selectedId);
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            {actionSelectionMode && actionSelectionFieldKey === key && (
              <Box sx={{ mt: 1, p: 2, bgcolor: isDark ? "#1a3a1a" : "#e8f5e8", borderRadius: 1, border: "2px solid #4caf50", textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Click an action node in the workflow to select it</Typography>
                <Button size="small" variant="outlined" onClick={() => { setActionSelectionMode(false); setActionSelectionFieldKey(null); }}>Cancel</Button>
              </Box>
            )}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => { setActionSelectionMode(true); setActionSelectionFieldKey(key); }}
              disabled={actionSelectionMode && actionSelectionFieldKey !== key}
              sx={{ mt: 1, borderColor: isDark ? "#444" : "#ccc", color: isDark ? "#fff" : "#222" }}
            >
              {selectedWf ? `Selected: ${selectedWf.name}` : "Click to Select Action"}
            </Button>
          </Box>
        );
      }
      if (field.type === "agent") {
        const selectedValue = formValues[key] || "";
        if (!selectedValue && field.selectedValue) {
          setFormValues((v) => ({ ...v, [key]: field.selectedValue }));
        }
        const selectedAgent = aiAgents.find((a) => a._id === selectedValue) || null;
        return (
          <Box key={key} sx={{ mb: 2, ml: 2, mr: 1 }}>
            <Typography variant="caption" sx={{ color: isDark ? "#bbb" : "#666", fontWeight: 500, mb: 0.5, display: "block" }}>
              {field.label}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedValue}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: isDark ? "#1e2328" : "#fafafa",
                },
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val) => {
                  const agent = aiAgents.find((a) => a._id === val) || null;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SmartToyIcon sx={{ fontSize: 16, color: agent ? "primary.main" : (isDark ? "#666" : "#aaa") }} />
                      <Typography variant="body2" sx={{ color: agent ? "text.primary" : (isDark ? "#888" : "#aaa") }}>
                        {agent ? agent.name : "Default agent"}
                      </Typography>
                      {agent?.isDefault && (
                        <Typography variant="caption" sx={{
                          ml: 0.5, px: 0.8, py: 0.1,
                          bgcolor: isDark ? "#1a3a2a" : "#e8f5e9",
                          color: isDark ? "#66bb6a" : "#2e7d32",
                          borderRadius: 1, fontSize: 10, fontWeight: 600,
                        }}>
                          default
                        </Typography>
                      )}
                    </Box>
                  );
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: isDark ? "#666" : "#aaa" }} />
                  <Typography variant="body2" color="text.secondary">Default agent</Typography>
                </Box>
              </MenuItem>
              {aiAgentsLoading && (
                <MenuItem disabled sx={{ opacity: 0.5, py: 0.5 }}>
                  <Typography variant="caption">Loading agents…</Typography>
                </MenuItem>
              )}
              {!aiAgentsLoading && aiAgents.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.5, py: 0.25 }}>
                  <Typography variant="caption">— select a specific agent —</Typography>
                </MenuItem>
              )}
              {aiAgents.map((agent) => (
                <MenuItem key={agent._id} value={agent._id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <SmartToyIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{agent.name}</Typography>
                    {agent.isDefault && (
                      <Typography variant="caption" sx={{
                        px: 0.8, py: 0.1,
                        bgcolor: isDark ? "#1a3a2a" : "#e8f5e9",
                        color: isDark ? "#66bb6a" : "#2e7d32",
                        borderRadius: 1, fontSize: 10, fontWeight: 600,
                      }}>
                        default
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" sx={{ color: isDark ? "#666" : "#999", mt: 0.5, display: "block" }}>
              {selectedAgent
                ? `Using "${selectedAgent.name}" — overrides workspace default for this action`
                : "Uses the workspace default agent. This agent replies via DM only."}
            </Typography>
          </Box>
        );
      }
      if (field.type === "teammember") {
        if (!teamMembers?.length && !settingsLoading) dispatch(fetchTeamMembers());
        let val = formValues[key] ?? (field.selectedValues?.[0]);
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <select
              value={val ?? ""}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
              style={{
                width: "90%",
                marginTop: 4,
                padding: "8px 10px",
                borderRadius: 6,
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                background: isDark ? "#23272b" : "#fff",
                color: isDark ? "#fff" : "#222",
              }}
            >
              <option value="">Select a team member</option>
              {(teamMembers || []).map((m) => (
                <option key={m.id || m._id} value={m.id || m._id || m.email}>{m.email}</option>
              ))}
            </select>
          </Box>
        );
      }
      if (field.type === "multipleteammember") {
        if (!teamMembers?.length && !settingsLoading) dispatch(fetchTeamMembers());
        let ids = formValues[key] ?? field.selectedValues ?? [];
        const selectedMembers = (Array.isArray(ids) ? ids : [ids])
          .map((id) => teamMembers?.find((m) => m.id === id || m._id === id || m.email === id))
          .filter(Boolean);
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <Autocomplete
              multiple
              options={teamMembers || []}
              getOptionLabel={(o) => o.email || o.name || ""}
              value={selectedMembers}
              onChange={(_, newVal) =>
                setFormValues((v) => ({ ...v, [key]: newVal.map((m) => m.id || m._id || m.email) }))
              }
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select members" />}
              sx={{ width: "90%", mt: 1, background: isDark ? "#23272b" : "#fff", borderRadius: 1 }}
            />
          </Box>
        );
      }
      if (field.type === "team") {
        if (!teams?.length && !settingsLoading) dispatch(fetchTeams());
        let val = formValues[key] ?? (field.selectedValues?.[0]);
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
            <select
              value={val ?? ""}
              onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
              style={{
                width: "90%",
                marginTop: 4,
                padding: "8px 10px",
                borderRadius: 6,
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                background: isDark ? "#23272b" : "#fff",
                color: isDark ? "#fff" : "#222",
              }}
            >
              <option value="">Select a team</option>
              {(teams || []).map((t) => (
                <option key={t.id || t._id} value={t.id || t._id || t.teamName}>{t.teamName}</option>
              ))}
            </select>
          </Box>
        );
      }
      if (field.type === "catalog") {
        const catalogVal = formValues[key] ?? (field.selectedValue && typeof field.selectedValue === "object" ? field.selectedValue : null);
        const handleSelectCatalog = (catalog) => {
          setFormValues((v) => ({ ...v, [key]: catalog }));
        };
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}>Select Catalog & Products</Typography>
            {catalogsStatus === "loading" && (
              <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading catalogs...</Typography>
              </Box>
            )}
            {catalogsStatus === "failed" && (
              <Typography variant="body2" color="error">Failed to load catalogs. Check your connection.</Typography>
            )}
            <CatalogSend platform="facebook" onCatalogSelect={handleSelectCatalog} selectedCatalog={catalogVal} />
          </Box>
        );
      }
      return null;
    });
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        height: "100%",
        width: 400,
        maxWidth: "95vw",
        bgcolor: isDark ? "#23272b" : "#fff",
        boxShadow: 6,
        zIndex: 30,
        borderLeft: `1.5px solid ${isDark ? "#444" : "#e0e0e0"}`,
        p: 1,
        display: "flex",
        flexDirection: "column",
        color: isDark ? theme.palette.text.primary : "inherit",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selectedAction && (
            <IconButton onClick={() => { setSelectedAction(null); setLocalConfig(null); }} sx={{ mr: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <BuildCircleIcon sx={{ color: MESSENGER_COLOR, fontSize: 28, mr: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            {selectedAction ? "Facebook action" : "Choose a Facebook action"}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Box
        ref={drawerBodyScrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          pb: selectedAction ? 0 : 12,
          scrollbarWidth: { xs: "none", sm: "auto" },
          msOverflowStyle: { xs: "none", sm: "auto" },
          "&::-webkit-scrollbar": {
            width: { xs: 0, sm: 8 },
            height: { xs: 0, sm: 8 },
          },
        }}
      >
        {selectedAction ? (
          <>
            {isBusinessHoursBranchAction(selectedAction.name) ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Splits the workflow: the YES path runs when the current time is inside the schedule below, the NO path runs otherwise.
                </Typography>
                <BusinessHoursEditor
                  value={formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE}
                  onChange={(v) => setFormValues((prev) => ({ ...prev, "0-Configuration": v }))}
                  expandedDay={businessHoursExpandedDay}
                  onExpandedDayChange={setBusinessHoursExpandedDay}
                />
              </Box>
            ) : isSetContactFieldsAction(selectedAction.name) ? (
              <Box sx={{ mb: 3 }}>
                <SetContactFieldsEditor
                  value={formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_SET_CONTACT_FIELDS_VALUE}
                  onChange={(v) => setFormValues((prev) => ({ ...prev, "0-Configuration": v }))}
                  workspaceId={workspaceId}
                />
              </Box>
            ) : isContactFieldsBranchAction(selectedAction.name) ? (
              <Box sx={{ mb: 3 }}>
                <ContactFieldsEditor
                  value={formValues["0-Configuration"] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE}
                  onChange={(v) => setFormValues((prev) => ({ ...prev, "0-Configuration": v }))}
                  workspaceId={workspaceId}
                />
              </Box>
            ) : (
              localConfig?.map((cfg, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}>{cfg.name}</Typography>
                  {renderFields(cfg.fields, `${idx}-`, idx)}
                </Box>
              ))
            )}

            {selectedAction.category === "Message" && !selectedAction.name?.toLowerCase().includes("go to") && (
              <>
                <Box sx={{ mb: 3, mt: 3, p: 2, border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`, borderRadius: 2, background: isDark ? "#2e3440" : "#f9f9f9" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Keywords</Typography>
                    <Button size="small" variant="outlined" onClick={() => setKeywords([...keywords, { keyword: "", targetId: "" }])}>+ Add Keyword</Button>
                  </Box>
                  {keywords.length === 0 ? (
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: isDark ? "#888" : "#999", textAlign: "center", py: 2 }}>No keywords. Click "Add Keyword" to add one.</Typography>
                  ) : (
                    keywords.map((kw, i) => (
                      <Box key={i} sx={{ mb: 2, p: 2, border: `1px solid ${isDark ? "#555" : "#ddd"}`, borderRadius: 1 }}>
                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Keyword"
                            value={kw.keyword ?? ""}
                            onChange={(e) => {
                              const n = [...keywords];
                              n[i] = { ...n[i], keyword: e.target.value };
                              setKeywords(n);
                            }}
                          />
                          <IconButton size="small" onClick={() => setKeywords(keywords.filter((_, j) => j !== i))}><CloseIcon fontSize="small" /></IconButton>
                        </Box>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={() => { setKeywordTargetSelectionMode(true); setKeywordTargetSelectionIndex(i); }}
                        >
                          {kw.targetId ? (extractWorkflowMessages.find((m) => m.id === kw.targetId)?.name ?? "Selected") : "Select target message"}
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
                {keywords.length > 0 && (
                  <Box sx={{ mb: 3, p: 2, border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>After sending this message</Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="radio" checked={!waitForKeyword} onChange={() => setWaitForKeyword(false)} />
                        Send next message immediately
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="radio" checked={waitForKeyword} onChange={() => setWaitForKeyword(true)} />
                        Wait for keyword reply
                      </label>
                    </Box>
                  </Box>
                )}
                <Box sx={{ mb: 3, p: 2, border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Incorrect Keyword Message</Typography>
                    <Checkbox checked={incorrectKeywordMessage.enabled} onChange={(e) => setIncorrectKeywordMessage((c) => ({ ...c, enabled: e.target.checked }))} />
                  </Box>
                  {incorrectKeywordMessage.enabled && (
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="I didn't understand that. Please try again."
                      value={incorrectKeywordMessage.text ?? ""}
                      onChange={(e) => setIncorrectKeywordMessage((c) => ({ ...c, text: e.target.value }))}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </>
            )}

            <Box sx={{ position: "sticky", bottom: 0, background: isDark ? "#23272b" : "#fff", borderTop: `1px solid ${isDark ? "#444" : "#e0e0e0"}`, py: 2, mt: 2 }}>
              <Button
                variant="contained"
                sx={{ borderRadius: "20px", backgroundColor: MESSENGER_COLOR, color: "#fff", fontWeight: 600, "&:hover": { backgroundColor: "#0066CC" } }}
                onClick={handleSaveAction}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </>
        ) : (
          FACEBOOK_ACTIONS_BY_CATEGORY.map((cat) => (
            <Accordion
              key={cat.category}
              expanded={expanded === cat.category}
              onChange={(_, isExp) => setExpanded(isExp ? cat.category : false)}
              sx={{
                bgcolor: isDark ? "#23272b" : "#f5f5f5",
                mb: 1,
                boxShadow: 0,
                border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {CATEGORY_ICONS[cat.category] || <CategoryIcon sx={{ mr: 1, color: MESSENGER_COLOR }} />}
                  <Typography fontWeight={600}>{cat.category}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                {cat.actions.map((action) => {
                  const isBlockedFirstAction =
                    isCommentTriggerFirstSlot && COMMENT_TRIGGER_INCOMPATIBLE_ACTIONS.has(action.name);
                  return (
                    <Paper
                      key={action.name}
                      elevation={0}
                      title={isBlockedFirstAction ? COMMENT_TRIGGER_BLOCKED_TOOLTIP : undefined}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: isBlockedFirstAction ? (isDark ? "#1c1f22" : "#f0f0f0") : (isDark ? "#23272b" : "#fff"),
                        border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                        cursor: isBlockedFirstAction ? "not-allowed" : "pointer",
                        opacity: isBlockedFirstAction ? 0.5 : 1,
                        "&:hover": isBlockedFirstAction
                          ? {}
                          : { borderColor: MESSENGER_COLOR, background: isDark ? "#2e3440" : "#e7f3ff" },
                      }}
                      onClick={() => {
                        if (isBlockedFirstAction) return;
                        setSelectedAction(action);
                        const fv = {};
                        (action.config || []).forEach((cfg, i) => {
                          (cfg.fields || []).forEach((f) => {
                            const k = `${i}-${f.label || f.name}`;
                            if (f.type === "checkbox") fv[k] = f.selectedValue ?? false;
                            else if (f.type === "number") fv[k] = f.selectedValue ?? 30;
                            else if (f.type === "select" && f.options?.length)
                              fv[k] = typeof f.options[0] === "object" ? f.options[0].label : f.options[0];
                            else if (f.type === "text large" || f.type === "text") fv[k] = "";
                          });
                        });
                        setFormValues(fv);
                        setKeywords([]);
                        setWaitForKeyword(false);
                        setIncorrectKeywordMessage({ enabled: false, text: "I didn't understand that. Please try again.", showOptions: false });
                      }}
                    >
                      <Typography fontWeight={700} fontSize={16} sx={isBlockedFirstAction ? { color: isDark ? "#777" : "#999" } : undefined}>
                        {action.name}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary">{action.description}</Typography>
                      {isBlockedFirstAction && (
                        <Typography fontSize={12} sx={{ mt: 0.5, color: isDark ? "#e0a030" : "#b26a00", fontStyle: "italic" }}>
                          Not available as the first reply to a comment
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ChooseFacebookActionDrawer;
